import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Newspaper,
  Calendar,
  Eye,
  EyeOff,
  Image as ImageIcon,
  FileText,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewsManager = ({ authToken }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category: 'news',
    image: '',
    documents: [],
    author: '',
    isPublished: true,
    publishedAt: ''  // Empty means use current date
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  // Create auth header from token
  const authHeader = {
    headers: { 'Authorization': `Basic ${authToken}` }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/news`, authHeader);
      setArticles(response.data);
    } catch (err) {
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const payload = {
      ...formData,
      document: null,
      documentName: null
    };
    // Convert publishedAt to ISO format if provided, otherwise let backend use current time
    if (formData.publishedAt) {
      payload.publishedAt = new Date(formData.publishedAt).toISOString();
    } else {
      payload.publishedAt = null;
    }
    return payload;
  };

  const handleCreate = async () => {
    try {
      const response = await axios.post(`${API}/admin/news`, buildPayload(), authHeader);
      if (response.data.success) {
        fetchArticles();
        setIsCreating(false);
        resetForm();
      }
    } catch (err) {
      console.error('Error creating article:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await axios.put(
        `${API}/admin/news/${editingArticle.id}`,
        buildPayload(),
        authHeader
      );
      if (response.data.success) {
        fetchArticles();
        setEditingArticle(null);
        resetForm();
      }
    } catch (err) {
      console.error('Error updating article:', err);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      const response = await axios.delete(`${API}/admin/news/${articleId}`, authHeader);
      if (response.data.success) {
        fetchArticles();
      }
    } catch (err) {
      console.error('Error deleting article:', err);
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      const response = await axios.put(
        `${API}/admin/news/${article.id}`,
        { isPublished: !article.isPublished },
        authHeader
      );
      if (response.data.success) {
        fetchArticles();
      }
    } catch (err) {
      console.error('Error toggling publish status:', err);
    }
  };

  const startEdit = (article) => {
    // Build documents array, merging legacy single-document field if present
    let docs = Array.isArray(article.documents) ? [...article.documents] : [];
    if (article.document && !docs.some(d => d.url === article.document)) {
      docs.unshift({ url: article.document, name: article.documentName || 'Document' });
    }
    // Format the publishedAt date for the datetime-local input
    const pubDate = article.publishedAt ? new Date(article.publishedAt).toISOString().slice(0, 16) : '';
    setEditingArticle(article);
    setFormData({
      title: article.title,
      summary: article.summary,
      content: article.content,
      category: article.category,
      image: article.image || '',
      documents: docs,
      author: article.author || '',
      isPublished: article.isPublished,
      publishedAt: pubDate
    });
    setIsCreating(false);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingArticle(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      content: '',
      category: 'news',
      image: '',
      documents: [],
      author: '',
      isPublished: true,
      publishedAt: ''
    });
  };

  const cancelEdit = () => {
    setEditingArticle(null);
    setIsCreating(false);
    resetForm();
    setUploadError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        setUploadError('');
      } else {
        const error = await response.json();
        setUploadError(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const allowedExts = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
    const MAX_SIZE = 20 * 1024 * 1024;

    setUploadingDoc(true);
    setUploadError('');

    const uploaded = [];
    const errors = [];

    for (const file of files) {
      const lowerName = file.name.toLowerCase();
      const isAllowed = allowedTypes.includes(file.type) || allowedExts.some(ext => lowerName.endsWith(ext));
      if (!isAllowed) {
        errors.push(`${file.name}: invalid type`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        errors.push(`${file.name}: exceeds 20MB`);
        continue;
      }

      try {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        const response = await fetch(`${API}/upload/document`, {
          method: 'POST',
          headers: { 'Authorization': `Basic ${authToken}` },
          body: uploadFormData
        });

        if (response.ok) {
          const data = await response.json();
          uploaded.push({ url: data.url, name: data.originalName || file.name });
        } else {
          const error = await response.json().catch(() => ({}));
          errors.push(`${file.name}: ${error.detail || 'upload failed'}`);
        }
      } catch (err) {
        console.error('Error uploading document:', err);
        errors.push(`${file.name}: network error`);
      }
    }

    if (uploaded.length > 0) {
      setFormData(prev => ({
        ...prev,
        documents: [...(prev.documents || []), ...uploaded]
      }));
    }

    setUploadError(errors.length > 0 ? errors.join('; ') : '');
    setUploadingDoc(false);

    // Reset input so the same file can be re-selected if needed
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const removeDocument = (idx) => {
    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).filter((_, i) => i !== idx)
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'news': return 'bg-blue-100 text-blue-800';
      case 'update': return 'bg-emerald-100 text-emerald-800';
      case 'announcement': return 'bg-purple-100 text-purple-800';
      case 'event': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">News & Updates</h2>
        <Button onClick={startCreate} className="bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-2" />
          Add Article
        </Button>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingArticle) && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="text-lg">
              {isCreating ? 'Create New Article' : 'Edit Article'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Article title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500"
                >
                  <option value="news">News</option>
                  <option value="update">Update</option>
                  <option value="announcement">Announcement</option>
                  <option value="event">Event</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Summary *</label>
              <Textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Brief summary of the article"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Full article content"
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: paste a URL like <code className="px-1 bg-gray-100 rounded">https://example.com</code> and it will become clickable. To use custom link text, type <code className="px-1 bg-gray-100 rounded">[click here](https://example.com)</code>.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                
                {/* Image Preview */}
                {formData.image && (
                  <div className="mb-3 relative inline-block">
                    <img 
                      src={formData.image} 
                      alt="Article preview" 
                      className="w-32 h-24 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="96" viewBox="0 0 128 96"><rect fill="%23f3f4f6" width="128" height="96"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">Error</text></svg>';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, image: ''})}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Upload Options */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-500 mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-3 h-3 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-gray-500">or</span>
                  </div>
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                  {uploadError && (
                    <p className="text-xs text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {uploadError}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Author name"
                />
              </div>
            </div>

            {/* Document Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachments (PDF / Word / Excel) — multiple files supported
              </label>

              {/* Documents List */}
              {formData.documents && formData.documents.length > 0 && (
                <div className="mb-3 space-y-2">
                  {formData.documents.map((doc, idx) => (
                    <div
                      key={`${doc.url}-${idx}`}
                      data-testid={`news-doc-item-${idx}`}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <FileText className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.name || 'Document'}</p>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          View document
                        </a>
                      </div>
                      <button
                        type="button"
                        data-testid={`news-doc-remove-${idx}`}
                        onClick={() => removeDocument(idx)}
                        className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600 flex-shrink-0"
                        aria-label="Remove document"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Document Upload */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    ref={docInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    data-testid="news-doc-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => docInputRef.current?.click()}
                    disabled={uploadingDoc}
                    data-testid="news-doc-upload-btn"
                  >
                    {uploadingDoc ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-500 mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FileText className="w-3 h-3 mr-2" />
                        Upload Document(s)
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Supported formats: PDF, Word (.doc, .docx), Excel (.xls, .xlsx) — Max 20MB per file. You can select multiple files at once or upload more in additional batches.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Publish Date
                </label>
                <Input
                  type="datetime-local"
                  value={formData.publishedAt}
                  onChange={(e) => setFormData({ ...formData, publishedAt: e.target.value })}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use current date/time. Set a past date to backdate the article.
                </p>
              </div>
              <div className="flex items-center">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isPublished"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                    className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <label htmlFor="isPublished" className="text-sm text-gray-700">
                    Publish immediately
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                onClick={isCreating ? handleCreate : handleUpdate}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Save className="w-4 h-4 mr-2" />
                {isCreating ? 'Create Article' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={cancelEdit}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Articles List */}
      <div className="space-y-4">
        {articles.length === 0 ? (
          <Card className="p-8 text-center">
            <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No articles yet</h3>
            <p className="text-gray-500 mb-4">Create your first news article to get started.</p>
            <Button onClick={startCreate} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Article
            </Button>
          </Card>
        ) : (
          articles.map((article) => (
            <Card key={article.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Badge className={getCategoryColor(article.category)}>
                        {article.category}
                      </Badge>
                      {article.isPublished ? (
                        <Badge className="bg-green-100 text-green-800">Published</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">Draft</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{article.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{article.summary}</p>
                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <span className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(article.publishedAt)}
                      </span>
                      {article.author && <span>By {article.author}</span>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTogglePublish(article)}
                      title={article.isPublished ? 'Unpublish' : 'Publish'}
                    >
                      {article.isPublished ? (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Eye className="w-4 h-4 text-emerald-500" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(article)}
                    >
                      <Edit className="w-4 h-4 text-blue-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(article.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
